import { useState, useEffect, useRef } from "react";
import "../Leads.css";
import { RefreshCw } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { mapLeadData } from "../../../utils/common";
import { COMPAIRABLES as columns } from "../../../utils/column_maps";
import axiosInstance from "../../../api/axiosInstance";

// Skeleton row component
const SkeletonRow = ({ columns }) => {
  return (
    <tr className="animate-pulse even:bg-gray-50">
      {columns.map((col, idx) => (
        <td
          key={col.key}
          className={`!border !border-gray-300 !px-4 !py-2 ${idx === 0 ? "sticky left-0 bg-inherit z-[1]" : ""}`}
          style={{
            minWidth: `${col.width}px`,
            width: `${col.width}px`,
            textAlign: col.cellAlign,
          }}
        >
          <div className="h-4 bg-gray-200 rounded w-full"></div>
        </td>
      ))}
    </tr>
  );
};

const Compairables = () => {
  const [fetchLoading, setFetchLoading] = useState(true);
  const leadsPerPage = 25;
  const [totalLeads, setTotalLeads] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);

  const [loadingZips, setLoadingZips] = useState({});
  const [isZipProcessing, setIsZipProcessing] = useState(false);
  const [reviewLoading, setReviewLoading] = useState({});
  const [leads, setLeads] = useState([]);

  // useEffect(() => {
  //   console.log(leads);
  // }, [leads]);

  // ----- Data fetching -----
  const fetchingRef = useRef(false);

  const fetchLeads = async (page = currentPage, showLoader = true) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      if (showLoader) setFetchLoading(true);
      const offset = (page - 1) * leadsPerPage;
      const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/leads?limit=${leadsPerPage}&offset=${offset}&status=contact_saved&sortBy=id&sortOrder=DESC&excludeAllWrongPhones=true&ai_data=true`);
      const data = await resp.data;
      if (data.success) {
        const sortedLeads = (data.leads || []).sort((a, b) => (b.id || 0) - (a.id || 0));

        // Merge with previous leads to preserve market data
        setLeads((prevLeads) => {
          const prevMap = Object.fromEntries(prevLeads.map((l) => [l.id, l]));
          return sortedLeads.map((newLead) => {
            const prev = prevMap[newLead.id];
            return {
              ...newLead,
              // Preserve existing marketData and loading flag if they exist
              marketData: prev?.marketData ?? newLead.marketData,
              marketDataLoading: prev?.marketDataLoading ?? false,
            };
          });
        });

        setTotalLeads(data.total);

        // Prepare lists for market data
        const addressList = sortedLeads.map((l) => l.full_address).filter(Boolean);
        const zipList = [...new Set(sortedLeads.map((l) => l.zip).filter(Boolean))];

        if (addressList.length && zipList.length) {
          fetchMarketDataBatch(addressList, zipList, showLoader);
        }
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setFetchLoading(false);
      fetchingRef.current = false;
    }
  };

  const fetchMarketDataBatch = async (addressList = [], zipList = [], showLoader = true) => {
    if (!addressList?.length || !zipList?.length) return;

    // Only show loading indicators if explicitly requested
    if (showLoader) {
      setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: true } : lead)));
    }

    try {
      const response = await axiosInstance.post(`/api/webhooks/market-data-batch`, {
        full_address: addressList,
      });

      if (response.data?.success) {
        let marketDataMap = {};
        if (Array.isArray(response.data.data)) {
          marketDataMap = Object.fromEntries(response.data.data.map((item) => [item.zip, item]));
        }

        setLeads((prevLeads) =>
          prevLeads.map((lead) => {
            const marketData = marketDataMap[lead.zip];
            // Always update marketData and clear loading for that lead
            return {
              ...lead,
              marketData: marketData ? marketData.normalizedMarket : lead.marketData,
              marketDataLoading: false,
            };
          }),
        );
      } else {
        // On API failure, clear loading for all affected leads
        setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: false } : lead)));
      }
    } catch (error) {
      console.error("Failed to fetch market data", error);
      setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: false } : lead)));
    }
  };

  // eslint-disable-next-line
  const fetchCbsaBatch = async (zips) => {
    if (!zips?.length) return;

    try {
      const response = await axiosInstance.post(`/api/dealmachine_properties/cbsa`, { zips });

      if (response.data?.success) {
        let cbsaMap = {};

        if (Array.isArray(response.data.data)) {
          cbsaMap = Object.fromEntries(response.data.data.map((item) => [item.zip, item]));
        } else if (response.data.data && typeof response.data.data === "object") {
          // New format: object keyed by zip
          cbsaMap = response.data.data;
        }

        if (Object.keys(cbsaMap).length > 0) {
          setLeads((prevLeads) =>
            prevLeads.map((lead) => {
              const cbsa = cbsaMap[lead.zip];
              return cbsa ? { ...lead, cbsaInfo: cbsa } : lead;
            }),
          );
        }
      }
    } catch (error) {
      console.error("Failed to fetch CBSA data", error);
    }
  };

  // ----- Effects -----
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", currentPage);
    setSearchParams(newParams, { replace: true });
  }, [currentPage, setSearchParams, searchParams]);

  useEffect(() => {
    fetchLeads(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!fetchingRef.current && !isZipProcessing) {
        fetchLeads(currentPage, false);
      }
    }, 120000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage]);

  // ----- Individual handlers -----
  const handleZipCounts = async (zip) => {
    if (loadingZips[zip]) return;

    setIsZipProcessing(true);
    setLoadingZips((prev) => ({ ...prev, [zip]: true }));

    try {
      const { data } = await axiosInstance.post("/api/webhooks/zip-stats", { zip, fetchNew: true });

      if (data?.success && data?.data) {
        const newStats = data.data;
        setLeads((prev) => prev.map((lead) => (lead.zip === zip ? { ...lead, zipStats: { ...lead.zipStats, ...newStats } } : lead)));
      }
    } catch (error) {
      console.error("error loading handlezipcounts:", error);
    } finally {
      setLoadingZips((prev) => ({ ...prev, [zip]: false }));
      setTimeout(() => setIsZipProcessing(false), 300);
    }
  };

  const handleZipMarketOverview = async (zip) => {
    if (loadingZips[zip]) return;
    setIsZipProcessing(true);
    setLoadingZips((prev) => ({ ...prev, [zip]: true }));
    try {
      await axiosInstance.post("/api/webhooks/zip-market-overview", { zip, fetchNew: true });
    } catch (error) {
      console.error("error loading handleZipMarketOverview:", error);
    } finally {
      setLoadingZips((prev) => ({ ...prev, [zip]: false }));
      setTimeout(() => setIsZipProcessing(false), 500);
    }
  };

  const generateFannieMaeReview = async (lead) => {
    try {
      setReviewLoading((prev) => ({ ...prev, [lead.id]: true }));
      const { data } = await axiosInstance.post(`/api/webhooks/fannie-mae`, {
        APN: lead.apn,
      });
      if (data?.success && data?.data?.gorqReview) {
        setLeads((prev) => prev.map((item) => (item.id === lead.id ? { ...item, ai_review: data.data.gorqReview } : item)));
      }
    } catch (error) {
      console.error("Error generating Fannie Mae review:", error);
    } finally {
      setReviewLoading((prev) => ({ ...prev, [lead.id]: false }));
    }
  };

  // ----- Pagination -----
  const indexOfFirstLead = (currentPage - 1) * leadsPerPage;
  const indexOfLastLead = Math.min(indexOfFirstLead + leadsPerPage, totalLeads || 0);
  const totalPages = Math.ceil((totalLeads || 0) / leadsPerPage);
  const totalTableWidth = columns.reduce((sum, col) => sum + col.width, 0);

  return (
    <div className="px-4">
      {/* Table */}
      <div className="w-full overflow-auto rounded shadow-sm max-h-[75vh]">
        <table className="min-w-full border-collapse bg-white" style={{ width: `${totalTableWidth}px`, minWidth: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <thead className="sticky top-0 z-[2] bg-gray-200 shadow-sm">
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={col.key}
                  className={`border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700 ${idx === 0 ? "sticky left-0 z-[3] bg-gray-200" : ""}`}
                  style={{
                    minWidth: `${col.width}px`,
                    width: `${col.width}px`,
                    textAlign: col.headerAlign,
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!fetchLoading ? (
              leads.length > 0 ? (
                leads.map((lead, idx) => {
                  const row = mapLeadData(lead);
                  return (
                    <tr key={lead.id || idx} className={`hover:bg-gray-100 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                      {columns.map((col, colIdx) => (
                        <td
                          key={col.key}
                          className={`truncate border border-gray-300 px-4 py-2 text-sm ${colIdx === 0 ? "sticky left-0 z-[1] bg-inherit" : ""} ${row[col.key] === "Not Found" ? "text-red-500" : /(CONFIRMED)/i.test((row[col.key] ?? "").toString()) ? "text-green-600 font-medium" : /(NO-ANSWER)/i.test((row[col.key] ?? "").toString()) ? "text-yellow-600" : "text-black"}`}
                          style={{
                            minWidth: `${col.width}px`,
                            width: `${col.width}px`,
                            textAlign: col.cellAlign,
                            whiteSpace: "pre-line",
                          }}
                        >
                          {col.key === "reload_stats" ? (
                            lead.zip ? (
                              <div className="flex justify-center items-center">
                                <RefreshCw onClick={() => handleZipCounts(lead.zip)} className={`cursor-pointer text-gray-700 hover:text-black hover:scale-110 ease-in-out duration-100 ${loadingZips[lead.zip] ? "animate-spin pointer-events-none opacity-50" : ""}`} size={20} />
                              </div>
                            ) : null
                          ) : col.key === "reload_market_overview" ? (
                            lead.zip ? (
                              <div className="flex justify-center items-center">
                                <RefreshCw onClick={() => handleZipMarketOverview(lead.zip)} className={`cursor-pointer text-gray-700 hover:text-black hover:scale-110 ease-in-out duration-100 ${loadingZips[lead.zip] ? "animate-spin pointer-events-none opacity-50" : ""}`} size={20} />
                              </div>
                            ) : null
                          ) : col.key === "ai_review" && !row[col.key] ? (
                            <div className="flex justify-center items-center">
                              <button className="px-2 py-1 text-xs bg-[#008080] text-white rounded hover:bg-[#006666] disabled:opacity-50" disabled={reviewLoading[lead.id]} onClick={() => generateFannieMaeReview(lead)}>
                                {reviewLoading[lead.id] ? "Generating..." : "Generate AI Review"}
                              </button>
                            </div>
                          ) : (
                            row[col.key]
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className="border border-gray-300 p-4 text-center text-gray-500">
                    No leads found.
                  </td>
                </tr>
              )
            ) : (
              [...Array(5)].map((_, i) => <SkeletonRow key={i} columns={columns} />)
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-col items-center !mb-10 mt-6">
        <span className="text-sm text-gray-700">
          Showing <span className="font-semibold text-gray-900">{totalLeads ? indexOfFirstLead + 1 : 0}</span> to <span className="font-semibold text-gray-900">{indexOfLastLead}</span> of <span className="font-semibold text-gray-900">{totalLeads || 0}</span> Entries
        </span>

        <div className="inline-flex !mt-2 xs:mt-0">
          <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-s cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
            Prev
          </button>

          <button className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === 1 ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
            1
          </button>

          {currentPage > 3 && totalPages > 5 && <span className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">...</span>}

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((page) => page !== 1 && page !== totalPages && page >= currentPage - 1 && page <= currentPage + 1)
            .map((page) => (
              <button key={page} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-pointer ${currentPage === page ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => setCurrentPage(page)}>
                {page}
              </button>
            ))}

          {currentPage < totalPages - 2 && totalPages > 5 && <span className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">...</span>}

          {totalPages > 1 && (
            <button className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === totalPages ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
              {totalPages}
            </button>
          )}

          <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-e cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Compairables;
